/**
 * Lightweight guard against catastrophic-backtracking ("ReDoS") regexes.
 *
 * The validator runs `pattern` keywords taken from untrusted JSON Schemas —
 * the free `/validate` and `/deploy` endpoints are anonymous, so a hostile
 * pattern like `(a+)+$` against a crafted string could pin CPU. A synchronous
 * `RegExp` can't be time-boxed, so instead we refuse to *run* patterns that
 * are structurally dangerous:
 *
 *   - over-long patterns (a cheap complexity ceiling), and
 *   - patterns with star height >= 2 — an unbounded quantifier (`*`, `+`,
 *     `{n,}`) applied to a subexpression that itself contains an unbounded
 *     quantifier. That nesting is the classic exponential-backtracking shape.
 *
 * This is deliberately conservative and simple. It does NOT catch every
 * pathological pattern (e.g. overlapping alternation `(a|ab)+`), so callers
 * should also cap the length of the input tested. A pattern judged unsafe is
 * meant to be *skipped* by the caller (its constraint goes unenforced) rather
 * than throwing — never a crash, never a hang.
 */

/** Patterns longer than this are refused outright. */
const MAX_PATTERN_LENGTH = 1000;

export function isPatternSafe(pattern: string): boolean {
  if (pattern.length > MAX_PATTERN_LENGTH) return false;
  return !hasNestedUnboundedQuantifier(pattern);
}

interface GroupFrame {
  /** Does this group's body contain an unbounded quantifier (directly or via
   * a nested quantified group)? */
  hasQuantifier: boolean;
}

function hasNestedUnboundedQuantifier(src: string): boolean {
  const stack: GroupFrame[] = [];
  let inClass = false; // inside a [...] character class — metachars are literal there
  let i = 0;

  const markEnclosing = (): void => {
    const top = stack[stack.length - 1];
    if (top) top.hasQuantifier = true;
  };

  while (i < src.length) {
    const ch = src[i]!;

    if (ch === '\\') {
      i += 2; // skip the escaped character (and a trailing-backslash no-ops out)
      continue;
    }
    if (inClass) {
      if (ch === ']') inClass = false;
      i++;
      continue;
    }

    switch (ch) {
      case '[':
        inClass = true;
        i++;
        break;
      case '(':
        stack.push({ hasQuantifier: false });
        i++;
        break;
      case ')': {
        const frame = stack.pop();
        const quant = quantifierAt(src, i + 1);
        // A quantified group whose body already holds a quantifier => star
        // height >= 2 => catastrophic.
        if (quant.unbounded && frame?.hasQuantifier) return true;
        // The closed group contributes a quantifier to its parent's body if
        // it contained one, or if it is itself unbounded-quantified.
        if ((frame?.hasQuantifier ?? false) || quant.unbounded) markEnclosing();
        i += 1 + quant.length; // consume ')' plus any quantifier token
        break;
      }
      case '*':
      case '+':
        markEnclosing();
        i += 1 + lazyLen(src, i + 1);
        break;
      case '{': {
        const brace = parseBrace(src, i);
        if (brace.length === 0) {
          i++; // not a real quantifier — treat '{' as a literal
        } else {
          if (brace.unbounded) markEnclosing();
          i += brace.length + lazyLen(src, i + brace.length);
        }
        break;
      }
      default:
        i++;
        break;
    }
  }
  return false;
}

/** A lazy modifier `?` directly after a quantifier consumes one more char. */
function lazyLen(src: string, at: number): number {
  return src[at] === '?' ? 1 : 0;
}

interface Quant {
  /** Length of the quantifier token (0 if there is none here). */
  length: number;
  /** Whether it allows unbounded repetition (`*`, `+`, `{n,}`). */
  unbounded: boolean;
}

function quantifierAt(src: string, at: number): Quant {
  const ch = src[at];
  if (ch === '*' || ch === '+') return { length: 1 + lazyLen(src, at + 1), unbounded: true };
  if (ch === '?') return { length: 1 + lazyLen(src, at + 1), unbounded: false };
  if (ch === '{') {
    const brace = parseBrace(src, at);
    if (brace.length > 0) {
      return { length: brace.length + lazyLen(src, at + brace.length), unbounded: brace.unbounded };
    }
  }
  return { length: 0, unbounded: false };
}

interface Brace {
  /** Length of `{...}` including braces, or 0 if not a well-formed brace. */
  length: number;
  unbounded: boolean;
}

/** Parse a `{n}`, `{n,}`, or `{n,m}` repetition starting at `start` ('{'). */
function parseBrace(src: string, start: number): Brace {
  const close = src.indexOf('}', start + 1);
  if (close === -1) return { length: 0, unbounded: false };
  const body = src.slice(start + 1, close);
  // Must look like a repetition spec: digits, optional comma, optional digits.
  if (!/^\d*,?\d*$/.test(body) || body === '' || body === ',') {
    return { length: 0, unbounded: false };
  }
  const unbounded = body.includes(',') && body.split(',')[1] === '';
  return { length: close - start + 1, unbounded };
}
